import { useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';
import {
  useReportTableDataGridStore,
  PersistedReportColumn,
  initialReportColumns,
} from '@store';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { formatDate } from '@util/date';
import { Star, Https } from '@mui/icons-material';
import { Dropdown, DropdownMenuItem } from '@components';
import { t } from 'i18next';
import { reportTypeLabelMapper } from '@util/reportTypeLabelMapper';
import { ColumnDensity, ReportType } from '@common/types';
import {
  ReportingTableEntry,
  useReportingTable,
  ReportingTableFilters,
} from './useReportingTable';

interface IBuildReportColumnsParams {
  handleOpenDeleteModal: (id: string) => void;
  handleMakeReportFavorite: (id: string, isFavorite: boolean) => void;
  makeReportFavoriteIsPending: boolean;
  reportIdToFavorite: string;
  user_id?: string;
  handleEditReport: (id: string) => void;
  handleGenerateReportCSV: (row: ReportingTableEntry) => void;
  isDownloading: boolean;
  handleOpenHistoryModal: (id: string) => void;
  density?: ColumnDensity;
}

const buildReportColumns = ({
  handleOpenDeleteModal,
  handleMakeReportFavorite,
  makeReportFavoriteIsPending,
  reportIdToFavorite,
  user_id,
  handleEditReport,
  handleGenerateReportCSV,
  isDownloading,
  handleOpenHistoryModal,
  density,
}: IBuildReportColumnsParams): GridColDef<ReportingTableEntry>[] => [
  {
    field: 'favorite',
    headerName: '',
    width: 60,
    minWidth: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            handleMakeReportFavorite(params.row.id, params.row.favorite);
          }}
          loading={
            makeReportFavoriteIsPending && reportIdToFavorite === params.row.id
          }
          sx={{ height: '24px', width: '24px' }}
        >
          <Star
            color={params.row.favorite ? 'primary' : 'disabled'}
            sx={{
              display:
                makeReportFavoriteIsPending &&
                reportIdToFavorite === params.row.id
                  ? 'none'
                  : 'block',
            }}
          />
        </IconButton>
      </Box>
    ),
  },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TruncatedTextWithConditionalTooltip
          width='100%'
          variant='body2'
          text={params.row.name}
        />
        {density !== 'dense' && (
          <TruncatedTextWithConditionalTooltip
            width='100%'
            color='textSecondary'
            variant='caption'
            text={reportTypeLabelMapper(params.row.report_type as ReportType)}
          />
        )}
      </Box>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 200,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={params.row.description}
      />
    ),
  },
  {
    field: 'folder_name',
    headerName: 'Folder',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={params.row.folder.name}
      />
    ),
  },
  {
    field: 'organisation',
    headerName: 'Organization',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={params.row.folder.organization?.name}
      />
    ),
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.created_by?.name}
          variant='body2'
        />
        {density !== 'dense' && (
          <TruncatedTextWithConditionalTooltip
            color='textSecondary'
            variant='caption'
            text={formatDate(params.row.created_at)}
          />
        )}
      </Box>
    ),
  },
  {
    field: 'private',
    headerName: '',
    width: 60,
    minWidth: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params =>
      params.row.private ? (
        <Https color='disabled' sx={{ height: '24px' }} />
      ) : null,
  },
  {
    field: 'action',
    headerName: '',
    width: 60,
    minWidth: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params => {
      const dropdownMenu: DropdownMenuItem[] = [
        {
          iconName: 'none',
          label: t('reporting.report.table.edit'),
          onClick: () => handleEditReport(params.row.id),
          disabled: user_id !== params.row.created_by.id,
        },
        {
          iconName: 'none',
          label: t('reporting.report.table.export'),
          onClick: () => handleGenerateReportCSV(params.row),
          disabled: isDownloading,
        },
        {
          iconName: 'none',
          label: t('reporting.report.table.viewHistory'),
          onClick: () => handleOpenHistoryModal(params.row.id),
        },
        {
          iconName: 'none',
          label: t('reporting.report.table.delete'),
          onClick: () => handleOpenDeleteModal(params.row.id),
          borderTop: true,
          disabled: user_id !== params.row.created_by.id,
        },
      ];
      return <Dropdown menuList={dropdownMenu} actionMenu />;
    },
  },
];

interface IUseReportDataGridTableProps {
  filters: ReportingTableFilters;
  setFilters: (filters: ReportingTableFilters) => void;
  handleOpenDeleteModal: (id: string) => void;
  handleMakeReportFavorite: (id: string, isFavorite: boolean) => void;
  makeReportFavoriteIsPending: boolean;
  reportIdToFavorite: string;
}

export const useReportDataGridTable = ({
  filters,
  setFilters,
  handleOpenDeleteModal,
  handleMakeReportFavorite,
  makeReportFavoriteIsPending,
  reportIdToFavorite,
}: IUseReportDataGridTableProps) => {
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const apiRef = useGridApiRef();

  const {
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useReportTableDataGridStore();

  const {
    sort_by,
    sort_order,
    handleEditReport,
    handleGenerateReportCSV,
    isDownloading,
    user_id,
    isReportHistoryModalOpen,
    handleOpenHistoryModal,
    handleCloseHistoryModal,
    historyReportId,
    handleRowClick,
  } = useReportingTable({ filters, setFilters });

  const runtimeColumns = useMemo(
    () =>
      buildReportColumns({
        handleOpenDeleteModal,
        handleMakeReportFavorite,
        makeReportFavoriteIsPending,
        reportIdToFavorite,
        user_id,
        handleEditReport,
        handleGenerateReportCSV,
        isDownloading,
        handleOpenHistoryModal,
        density,
      }),
    [
      makeReportFavoriteIsPending,
      reportIdToFavorite,
      user_id,
      isDownloading,
      density,
    ],
  );

  const columns = buildGridColumns<ReportingTableEntry, PersistedReportColumn>(
    runtimeColumns,
    persistedColumns,
  );

  const toPersistedColumn = (
    col: GridColDef<ReportingTableEntry>,
  ): PersistedReportColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ ...filters, sort_by: 'name', sort_order: 'ASC' });
      return;
    }
    setFilters({
      ...filters,
      sort_by: field as ReportingTableFilters['sort_by'],
      sort_order: sort.toUpperCase() as 'ASC' | 'DESC',
    });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ ...filters, limit: model.pageSize, page: model.page + 1 });
  };

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    setHiddenColumns(
      columns.map(col => col.field).filter(field => model[field] === false),
    );
  };

  const handleResetAll = () => {
    resetView();
  };

  return {
    apiRef,
    filters,
    sort_by,
    sort_order,
    handleChangeOrder,
    onPageModelChange,
    columns,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange: setDensity,
    isResetModalOpen,
    setIsResetModalOpen,
    handleResetAll,
    initialSortFilters: { sort_by: 'name', sort_order: 'ASC' },
    initialColumns: initialReportColumns,
    isReportHistoryModalOpen,
    handleOpenHistoryModal,
    handleCloseHistoryModal,
    historyReportId,
    handleRowClick,
  };
};
